import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Table, TableBody, TablePagination, TableFooter, TableRow } from '@mui/material/';
import { FormElements } from '@custom-components';

import SortableTableHead from './SortableTableHead';
import * as Styles from './SortableTable.styles';
import { StyledButton } from './StyledButton';

class STable extends Component {
  static desc(a, b, orderBy) {
    let bValue = b.columns.find((column) => column.columnId === orderBy).value;
    if (typeof bValue === 'string') {
      bValue = bValue.toUpperCase();
    }
    let aValue = a.columns.find((column) => column.columnId === orderBy).value;
    if (typeof aValue === 'string') {
      aValue = aValue.toUpperCase();
    }

    if (bValue < aValue) {
      return -1;
    }
    if (bValue > aValue) {
      return 1;
    }
    return 0;
  }

  static stableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = cmp(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  static getSorting(order, orderBy) {
    if (!orderBy) {
      return () => 1;
    }
    return order === 'desc' ? (a, b) => STable.desc(a, b, orderBy) : (a, b) => -STable.desc(a, b, orderBy);
  }

  static columnLabel(name, value, maxWidth, minWidth) {
    return (
      <div style={{ maxWidth: maxWidth ? `${maxWidth}px` : null, minWidth: minWidth ? `${minWidth}px` : null }}>
        <Styles.ColumnLabel>{name}</Styles.ColumnLabel>
        {name === 'Room Status' && value === 'ACTIVE' && <Styles.ActiveColumnValue>{value}</Styles.ActiveColumnValue>}
        {name === 'Room Status' && value === 'INACTIVE' && (
          <Styles.InactiveColumnValue>{value}</Styles.InactiveColumnValue>
        )}
        {name !== 'Room Status' && <Styles.ColumnValue>{value}</Styles.ColumnValue>}
      </div>
    );
  }

  constructor(props) {
    super(props);

    const { defaultSort } = this.props;

    this.mx = 0;

    this.state = {
      order: 'asc',
      orderBy: defaultSort,
      page: 0,
      rowsPerPage: 10
    };
  }

  componentDidMount() {
    this.elementScroll = document.querySelector('.dragTable');
    this.elementScroll.addEventListener('mousedown', this.mouseDown);
    this.elementScroll.addEventListener('mouseup', this.mouseUp);
  }

  componentWillUnmount() {
    this.elementScroll.removeEventListener('mousedown', this.mouseDown);
    this.elementScroll.removeEventListener('mouseup', this.mouseUp);
  }

  getColumns(row) {
    const { columns, selectable } = this.props;

    const hasTag = row.hasTag ? 'true' : 'false';

    const cells = [];

    if (selectable) {
      cells.push(
        <Styles.CheckboxCell key="checkbox" className="checkbox">
          <div className="checkbox">
            <FormElements.Checkbox checked={row.selected} onChange={row.onSelect} />
          </div>
        </Styles.CheckboxCell>
      );
    }

    columns.forEach((column) => {
      const rowColumn = row.columns.find((rowInfo) => rowInfo.columnId === column.id);
      if (rowColumn) {
        cells.push(
          <Styles.Cell key={column.id} className={column.id} hastag={hasTag}>
            {rowColumn.tag && row.hasTag && <Styles.Tag>{rowColumn.tag}</Styles.Tag>}
            {rowColumn.label}
          </Styles.Cell>
        );
      }
    });

    return cells;
  }

  mouseDown = (e) => {
    this.elementScroll.sx = this.elementScroll.scrollLeft;
    this.mx = e.pageX - this.elementScroll.offsetLeft;

    this.elementScroll.addEventListener('mousemove', this.mouseMove);
  };

  mouseUp = () => {
    this.elementScroll.removeEventListener('mousemove', this.mouseMove);
    this.mx = 0;
  };

  mouseMove = (e) => {
    const mx2 = e.pageX - this.elementScroll.offsetLeft;
    if (this.mx) this.elementScroll.scrollLeft = this.elementScroll.sx + this.mx - mx2;
  };

  handleRequestSort = (event, property) => {
    const { orderBy, order } = this.state;

    const newOrderBy = property;
    let newOrder = 'desc';

    if (orderBy === property && order === 'desc') {
      newOrder = 'asc';
    }

    this.setState({ order: newOrder, orderBy: newOrderBy });
  };

  handleSelectAll = () => {
    const { onSelectAll } = this.props;
    onSelectAll();
  };

  changePage = (event, page) => {
    this.setState({ page });
  };

  changeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value });
  };

  render() {
    const { columns, rows, rowAlignTop, showHeader, pagination, selectable, allSelected, hideHeaderCheckbox } =
      this.props;
    const { order, orderBy, page, rowsPerPage } = this.state;

    const tableRows = STable.stableSort(rows, STable.getSorting(order, orderBy));

    return (
      <Styles.TableContainer className="dragTable">
        <Table>
          {showHeader && (
            <SortableTableHead
              columns={columns}
              order={order}
              orderBy={orderBy}
              onRequestSort={this.handleRequestSort}
              onSelectAll={this.handleSelectAll}
              allSelected={allSelected}
              selectable={selectable}
              hideHeaderCheckbox={hideHeaderCheckbox}
            />
          )}
          <TableBody>
            {(pagination ? tableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : tableRows).map(
              (row) => (
                <Fragment key={row.id}>
                  <Styles.Row hover tabIndex={-1} key={row.id} className={rowAlignTop ? 'top' : ''}>
                    {this.getColumns(row)}
                  </Styles.Row>
                  {row.auxilaryComponent && (
                    <Styles.Row>
                      <Styles.ExpandedCell colSpan={columns.length + 1}>{row.auxilaryComponent}</Styles.ExpandedCell>
                    </Styles.Row>
                  )}
                </Fragment>
              )
            )}
          </TableBody>
          {pagination && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={tableRows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={this.changePage}
                  onRowsPerPageChange={this.changeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </Styles.TableContainer>
    );
  }
}

STable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      numeric: PropTypes.bool,
      disablePadding: PropTypes.bool,
      label: PropTypes.string
    })
  ),
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      hasTag: PropTypes.bool,
      columns: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
          columnId: PropTypes.string,
          label: PropTypes.node,
          tag: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
        })
      )
    })
  ),
  defaultSort: PropTypes.string,
  rowAlignTop: PropTypes.bool,
  showHeader: PropTypes.bool,
  hideHeaderCheckbox: PropTypes.bool,
  pagination: PropTypes.bool,
  selectable: PropTypes.bool,
  allSelected: PropTypes.bool,
  onSelectAll: PropTypes.func
};

STable.defaultProps = {
  columns: [],
  rows: [],
  rowAlignTop: true,
  showHeader: true,
  selectable: false,
  allSelected: false,
  pagination: false,
  hideHeaderCheckbox: false,
  onSelectAll: () => {},
  defaultSort: ''
};

export { STable, Styles, StyledButton };
